"use client";

import AmbientCanvas from "./AmbientCanvas";

interface Ingredient {
  id: string;
  name: string;
  category: string;
  description: string;
  potency: number;
  color: string;
}

const ingredients: Ingredient[] = [
  { id: "nightshade", name: "Nightshade", category: "Botanical", description: "Harvested at midnight. Dark, toxic beauty.", potency: 92, color: "#7b5aa6" },
  { id: "amber", name: "Dark Amber", category: "Resin", description: "Fossilized resin. Warm, infinite depth.", potency: 88, color: "#c9a84c" },
  { id: "vetiver", name: "Smoked Vetiver", category: "Botanical", description: "Fire-cured roots. Earthy, smoky, hypnotic.", potency: 84, color: "#5b7d9b" },
  { id: "jasmine", name: "Jasmine Sambac", category: "Floral", description: "Night-blooming jasmine. Intoxicating, sensual.", potency: 90, color: "#c47a8b" },
  { id: "oud", name: "Dark Oud", category: "Resin", description: "Aged 40 years. Deep, transcendent.", potency: 96, color: "#8a5a2a" },
  { id: "tuberose", name: "Tuberose", category: "Floral", description: "Creamy, opulent, unforgettable.", potency: 86, color: "#d99aa6" },
];

export default function Alchemy() {
  return (
    <section
      id="alchemy"
      aria-label="The Alchemy — Ingredients"
      className="relative py-24 md:py-36 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #fbf8f2 0%, #f2ecdf 100%)" }}
    >
      {/* Ambient orbs */}
      <div
        className="orb-glow -top-40 left-1/4 w-[36rem] h-[36rem]"
        style={{ background: "radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 65%)" }}
        aria-hidden="true"
      />
      <div
        className="orb-glow -bottom-40 right-1/4 w-[36rem] h-[36rem]"
        style={{ background: "radial-gradient(circle, rgba(196,122,139,0.07) 0%, transparent 65%)", animationDelay: "-6s" }}
        aria-hidden="true"
      />
      <AmbientCanvas variant="dust" accent="#a8843a" density={0.5} className="opacity-35" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <p className="reveal-blur text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-4">
              Ingredients
            </p>
            <h2
              className="reveal-blur font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight text-[#221d17]"
              style={{ fontWeight: 300, transitionDelay: "0.1s" }}
            >
              The <span className="text-shimmer-gold">Alchemy</span>
            </h2>
          </div>
          <div className="max-w-sm">
            <p className="reveal-blur text-sm text-[#6f6759]/75 font-light leading-relaxed mb-4" style={{ transitionDelay: "0.2s" }}>
              Six heroes drawn from the rarest corners of the earth. Each one
              measured, aged, and blended in darkness.
            </p>
            <p className="reveal-blur font-mono text-[9px] tracking-[0.25em] uppercase text-[#6f6759]/50" style={{ transitionDelay: "0.25s" }}>
              Selectivity: top 0.02% of global harvest
            </p>
          </div>
        </div>

        {/* Grid — light glass essence cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ingredients.map((ingredient, index) => (
            <div
              key={ingredient.id}
              className="glass reveal-blur relative p-7 rounded-sm group gold-frame gold-glow-hover overflow-hidden"
              style={{ transitionDelay: `${index * 0.07}s` }}
              data-cursor="pointer"
            >
              {/* Hover particle visual */}
              <AmbientCanvas
                variant="petals"
                accent={ingredient.color}
                density={0.4}
                className="opacity-0 transition-opacity duration-700 group-hover:opacity-50 mix-blend-multiply"
              />

              {/* Top reflection line */}
              <div
                className="absolute top-0 left-8 right-8 h-px transition-all duration-700 group-hover:left-4 group-hover:right-4"
                style={{ background: `linear-gradient(90deg, transparent, ${ingredient.color}88, transparent)` }}
                aria-hidden="true"
              />

              {/* Category label */}
              <div className="flex items-center justify-between mb-5 relative">
                <span className="text-[8px] tracking-[0.3em] uppercase" style={{ color: `${ingredient.color}` }}>
                  {ingredient.category}
                </span>
                <span className="w-1.5 h-1.5 rotate-45" style={{ background: `${ingredient.color}88` }} aria-hidden="true" />
              </div>

              {/* Name */}
              <h3 className="font-serif text-2xl text-[#221d17] mb-2 relative" style={{ fontWeight: 300 }}>
                {ingredient.name}
              </h3>

              {/* Description */}
              <p className="text-xs text-[#6f6759]/70 font-light leading-relaxed mb-6 min-h-[3rem] relative">
                {ingredient.description}
              </p>

              {/* Potency bar */}
              <div className="flex items-center gap-4 relative">
                <div className="flex-1 h-px bg-[#221d17]/8 relative">
                  <div
                    className="absolute inset-y-0 left-0 transition-all duration-700"
                    style={{
                      width: `${ingredient.potency}%`,
                      background: `linear-gradient(90deg, transparent, ${ingredient.color})`,
                    }}
                  />
                </div>
                <span className="font-mono text-[10px]" style={{ color: `${ingredient.color}` }}>
                  {ingredient.potency}%
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="reveal-blur mt-12 flex items-center gap-4">
          <div className="w-8 h-px bg-[#c9a84c]/50" aria-hidden="true" />
          <p className="text-[9px] tracking-[0.3em] uppercase text-[#6f6759]/55">
            Every batch traced to origin · No synthetics · No shortcuts
          </p>
        </div>
      </div>
    </section>
  );
}